import {
  useInfiniteQuery,
  useQuery,
  keepPreviousData,
} from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'

import { APPOINTMENTS_AUTO_FETCH_MAX_PAGES } from '../config/constants'
import { AGENT_URL } from '../config/env'
import { ApiError } from '../lib/api'
import type { AppointmentRecord } from '../features/appointments/types'
import type { FilterCondition } from '../utils/filterUtils'
import { drainCursorPages } from '../lib/drainCursorPages'

export interface AppointmentsQueryParams {
  limit?: number
  sort_by?: string
  sort_order?: 'asc' | 'desc'
  // Structured filters serialized as filter[field][operator]=value (parsed +
  // validated server-side by relay's /data/appointments).
  filters?: FilterCondition[]
}

export interface AppointmentsResponse {
  data: AppointmentRecord[]
  pagination: {
    limit: number
    has_more: boolean
    next_cursor: string | null
    total: number | null
  }
}

/**
 * The `additional_info` column is stored as a JSON *string* (not jsonb), so
 * parse it into a plain object. Tolerates already-parsed objects, blank/invalid
 * strings, and non-object JSON (arrays/primitives) by returning null.
 */
function parseAdditionalInfo(value: unknown): Record<string, unknown> | null {
  if (!value) return null
  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return null
    try {
      const parsed = JSON.parse(trimmed)
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null
    } catch {
      return null
    }
  }
  return null
}

/** Appointment timestamps without offsets encode UTC; source dates are not instants. */
function normalizeAppointmentTimestamp(value: unknown): string | null {
  if (typeof value !== 'string') return null
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/.test(value)
    ? `${value}Z`
    : value
}

async function fetchAppointments(
  token: string,
  params: AppointmentsQueryParams,
  cursor?: string
): Promise<AppointmentsResponse> {
  const search = new URLSearchParams()
  if (params.limit) {
    search.set('limit', params.limit.toString())
  }
  if (cursor) {
    search.set('cursor', cursor)
  }
  if (params.sort_by) {
    search.set('sort_by', params.sort_by)
  }
  if (params.sort_order) {
    search.set('sort_order', params.sort_order)
  }
  if (params.filters && params.filters.length > 0) {
    for (const filter of params.filters) {
      const value = filter.value.trim()
      if (!value) continue
      search.append(`filter[${filter.field}][${filter.operator}]`, value)
    }
  }

  const url = `${AGENT_URL}/data/appointments?${search.toString()}`

  const response = await fetch(url, {
    headers: {
      // Match the calls/patients endpoints: pass Clerk JWT directly
      Authorization: token,
    },
  })

  if (!response.ok) {
    const errorText = await response.text()
    let errorMessage = `Failed to fetch appointments: ${response.status}`
    try {
      const errorData = JSON.parse(errorText)
      // relay validation errors surface as `detail`; hoku's own errors as `error`.
      errorMessage = errorData.detail || errorData.error || errorMessage
    } catch {
      if (errorText) errorMessage = errorText
    }
    throw new ApiError(errorMessage, response.status)
  }

  const raw: any = await response.json()
  const rawAppointments: any[] = Array.isArray(raw?.data) ? raw.data : []

  const data: AppointmentRecord[] = rawAppointments.map(
    (appointment: any): AppointmentRecord => {
      return {
        id: String(appointment.id ?? ''),
        created_at:
          typeof appointment.created_at === 'string'
            ? appointment.created_at
            : null,
        start_time: normalizeAppointmentTimestamp(appointment.start_time),
        end_time: normalizeAppointmentTimestamp(appointment.end_time),
        provider_name:
          typeof appointment.provider_name === 'string'
            ? appointment.provider_name
            : null,
        org_id:
          typeof appointment.org_id === 'string' ? appointment.org_id : null,
        duty_id:
          typeof appointment.duty_id === 'string' ? appointment.duty_id : null,
        status:
          typeof appointment.status === 'string' ? appointment.status : null,
        patient_name:
          typeof appointment.patient_name === 'string'
            ? appointment.patient_name
            : null,
        patient_id:
          typeof appointment.patient_id === 'string'
            ? appointment.patient_id
            : null,
        org_slug:
          typeof appointment.org_slug === 'string'
            ? appointment.org_slug
            : null,
        provider_id:
          typeof appointment.provider_id === 'string'
            ? appointment.provider_id
            : null,
        facility_id:
          typeof appointment.facility_id === 'string'
            ? appointment.facility_id
            : null,
        timeslot_id:
          typeof appointment.timeslot_id === 'string'
            ? appointment.timeslot_id
            : null,
        source_appointment_id:
          typeof appointment.source_appointment_id === 'string'
            ? appointment.source_appointment_id
            : null,
        appointment_type:
          typeof appointment.appointment_type === 'string'
            ? appointment.appointment_type
            : null,
        appointment_reason:
          typeof appointment.appointment_reason === 'string'
            ? appointment.appointment_reason
            : null,
        source_status:
          typeof appointment.source_status === 'string'
            ? appointment.source_status
            : null,
        source_name:
          typeof appointment.source_name === 'string'
            ? appointment.source_name
            : null,
        location:
          typeof appointment.location === 'string'
            ? appointment.location
            : null,
        source_appointment_date:
          typeof appointment.source_appointment_date === 'string'
            ? appointment.source_appointment_date
            : null,
        mrn: typeof appointment.mrn === 'string' ? appointment.mrn : null,
        source_patient_id:
          typeof appointment.source_patient_id === 'string'
            ? appointment.source_patient_id
            : null,
        additional_info: parseAdditionalInfo(appointment.additional_info),
      }
    }
  )

  const rawPagination = raw?.pagination ?? {}
  const limit =
    typeof rawPagination.limit === 'number'
      ? rawPagination.limit
      : (params.limit ?? 25)

  const has_more = Boolean(rawPagination.has_more)
  const next_cursor =
    typeof rawPagination.next_cursor === 'string'
      ? (rawPagination.next_cursor as string)
      : null

  const total =
    typeof rawPagination.total === 'number' ? rawPagination.total : null

  return {
    data,
    pagination: {
      limit,
      has_more,
      next_cursor,
      total,
    },
  }
}

export function useAppointmentsQuery(
  params: AppointmentsQueryParams = {},
  options: { enabled?: boolean } = {}
) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useInfiniteQuery({
    queryKey: ['appointments', organization?.id, params],
    enabled: options.enabled ?? true,
    queryFn: async ({ pageParam }) => {
      const token = await getToken()
      if (!token) {
        throw new Error('No authentication token available')
      }
      return fetchAppointments(token, params, pageParam as string | undefined)
    },
    getNextPageParam: (lastPage) =>
      lastPage.pagination.next_cursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    // Keep prior rows visible during a filtered refetch so the table doesn't
    // flash its empty ("no results") state while the request resolves.
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

export interface AppointmentsRangeResult {
  appointments: AppointmentRecord[]
  /** True when the page cap stopped the fetch before the cursor drained. */
  truncated: boolean
}

/**
 * Fetch a bounded appointments result set completely — every page walked
 * inside a single queryFn (`while (cursor)`), for consumers like the
 * calendar's visible date range that must render whole or say they couldn't.
 *
 * A plain useQuery rather than useInfiniteQuery on purpose: one request
 * lifecycle means one error path (a failing later page fails the query
 * instead of re-arming a drain loop), one loading state (no per-page dim
 * flicker), and an explicit `truncated` flag when the page cap is hit.
 * No placeholderData either — a range-scoped view can't meaningfully show
 * the previous range's rows, so a new key renders the loading state.
 */
export function useAppointmentsRangeQuery(
  params: AppointmentsQueryParams = {},
  options: { enabled?: boolean } = {}
) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useQuery({
    queryKey: ['appointments', 'range', organization?.id, params],
    enabled: !!organization?.id && (options.enabled ?? true),
    queryFn: async (): Promise<AppointmentsRangeResult> => {
      const token = await getToken()
      if (!token) {
        throw new Error('No authentication token available')
      }
      const { rows, truncated } = await drainCursorPages(
        (cursor) => fetchAppointments(token, params, cursor),
        APPOINTMENTS_AUTO_FETCH_MAX_PAGES
      )
      return { appointments: rows, truncated }
    },
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}
